import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import uuidv4 from 'uuid/v4';

export enum ErrorCode {
	ParseError = -32700,
	InvalidRequest = -32600,
	MethodNotFound = -32601,
	InvalidParams = -32602,
	InternalError = -32603,
}

function errorCodeToString(code: ErrorCode): string {
	switch (code) {
		case ErrorCode.ParseError:
			return 'parse error';
		case ErrorCode.InvalidRequest:
			return 'invalid request';
		case ErrorCode.MethodNotFound:
			return 'method not found';
		case ErrorCode.InvalidParams:
			return 'invalid params';
		case ErrorCode.InternalError:
			return 'internal error';
	}
}

export class HTTPClientSetupError extends Error {
	constructor(public methodName: string, public error: AxiosError) {
		super(`jsonrpc2: ${methodName}: service unavailable`);
	}
}

export class ServiceUnavailableError extends Error {
	constructor(public methodName: string, public error: AxiosError) {
		super(`jsonrpc2: ${methodName}: service unavailable`);
	}
}

export class DeadlineExceededError extends Error {
	constructor(public methodName: string, public error: AxiosError) {
		super(`jsonrpc2: ${methodName}: deadline exceeded`);
	}
}

export class RequestParserError extends Error {
	constructor(public methodName: string, public message: string) {
		super(`jsonrpc2: request parser: ${methodName}: ${message}`);
	}
}

export class ProtocolError extends Error {
	constructor(public methodName: string, public message: string) {
		super(`jsonrpc2: ${methodName}: ${message}`);
	}
}

export function isErrorResponse(maybe: any): maybe is ErrorResponse {
	try {
		jsonToResponseError(maybe);
		return true;
	} catch {
		return false;
	}
}

function findMessageForCallError(error: ErrorResponse | ErrorCode): string {
	if (isErrorResponse(error)) {
		return error.message;
	}

	return errorCodeToString(error);
}

export class CallError extends Error {
	constructor(public name: string, public error: ErrorResponse | ErrorCode) {
		super(`${name}: ${findMessageForCallError(error)}`);
	}
}

export interface Request {
	jsonrpc: string;
	id?: string | number | null;
	method: string;
	params?: any;
}

export interface ErrorResponse {
	code: number;
	message: string;
	data?: any;
}

function jsonToResponseError(raw: any): ErrorResponse {
	if (raw === null) {
		throw new Error('error is required');
	}

	if (typeof raw?.code !== 'number') {
		throw new Error('error code must be a integer');
	}

	switch (raw.code) {
		case ErrorCode.InternalError:
		case ErrorCode.InvalidParams:
		case ErrorCode.InvalidRequest:
		case ErrorCode.MethodNotFound:
		case ErrorCode.ParseError:
			break;
		default:
			if (raw.code <= -32000 && raw.code >= -32099) {
				break;
			}
			throw new Error('wrong error code');
	}

	if (typeof raw?.message !== 'string') {
		throw new Error('error message must be a string');
	}

	if (typeof raw?.code !== 'number') {
		throw new Error('error code must be a number');
	}

	const res: ErrorResponse = {
		message: raw.message,
		code: raw.code,
	};

	if (raw?.data) {
		res.data = raw.data;
	}

	return res;
}

export interface Response {
	jsonrpc: string;
	id: string | number | null;
	result?: any;
	error?: ErrorResponse;
}

export function jsonToResponse(
	name: string,
	idFromRequest: Request['id'] | Array<Request['id']>,
	raw: any
): Response | null {
	if (idFromRequest === undefined) {
		return null;
	}

	if (typeof raw?.jsonrpc !== 'string') {
		throw new ProtocolError(name, 'jsonrpc must be a string');
	}

	if (raw.jsonrpc !== '2.0') {
		throw new ProtocolError(name, 'jsonrpc version must be 2.0');
	}

	if (raw.error !== undefined) {
		const err = jsonToResponseError(raw.error);

		if (
			err.code === ErrorCode.ParseError ||
			err.code === ErrorCode.InvalidRequest
		) {
			if (raw.id !== null) {
				throw new ProtocolError(name, 'id must be null');
			}
		} else if (raw?.id !== idFromRequest) {
			throw new ProtocolError(name, 'id is not matching');
		}

		if (raw.result) {
			throw new ProtocolError(name, 'result must not exists');
		}

		return {
			jsonrpc: raw.jsonrpc,
			id: raw.id,
			error: err,
		};
	}

	if (raw.id === undefined) {
		throw new ProtocolError(name, 'id is is required');
	}

	if (raw?.id !== idFromRequest) {
		throw new ProtocolError(name, 'id is not matching');
	}

	if (raw.result === undefined) {
		throw new ProtocolError(name, 'result is required on success');
	}

	return {
		jsonrpc: raw.jsonrpc,
		id: raw.id,
		result: raw.result,
	};
}

type SendFn = (req: AxiosRequestConfig) => Promise<AxiosResponse<any>>;
type AsyncStorageSetter = (id: string) => void;

const handleAxiosError = (name: string, err: AxiosError): Error => {
	if (err.response) {
		// The request was made and the server responded with a status code
		// that falls out of the range of 2xx. This should never happend, expect
		// something is wrong on the server implementation.
		return new CallError(name, ErrorCode.InternalError);
	} else if (err.request) {
		// The request was made but no response was received
		// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
		// http.ClientRequest in node.js

		// Timeout
		if (err.code === 'ECONNABORTED') {
			return new DeadlineExceededError(name, err);
		}

		return new ServiceUnavailableError(name, err);
	}

	// Something happened in setting up the request that triggered an Error
	return new HTTPClientSetupError(name, err);
};

export class JSONRPCClient {
	private send: SendFn = axios.create({}).request;
	private asyncStorageSetter: AsyncStorageSetter | undefined;

	constructor(
		private url: string,
		sendFn?: SendFn,
		asyncStorageSetter?: AsyncStorageSetter
	) {
		if (sendFn) {
			this.send = sendFn;
		}
		if (asyncStorageSetter) {
			this.asyncStorageSetter = asyncStorageSetter;
		}
	}

	private name(method: string): string {
		return `${this.url}(${method})`;
	}

	_call(
		method: string,
		data?: any,
		isNotification: boolean = false
	): Promise<Response | null> {
		if (method.length === 0) {
			throw new RequestParserError(
				this.name(method),
				'method must be at least one character long'
			);
		}

		if (data) {
			if (typeof data !== 'object' && !Array.isArray(data)) {
				throw new RequestParserError(
					this.name(method),
					'data must be either a object or array'
				);
			}
		}

		let id = undefined;
		if (!isNotification) {
			id = uuidv4();
		}

		const body: Request = {
			jsonrpc: '2.0',
			method: method,
			id: id,
		};

		if (data) {
			body.params = data;
		}

		const rq: AxiosRequestConfig = {
			url: this.url,
			method: 'POST',
			data: body,
		};

		return this.send(rq)
			.then((rp: AxiosResponse) => {
				let rpcRp;
				try {
					rpcRp = jsonToResponse(this.name(method), id, rp.data);
				} catch (e) {
					throw new CallError(
						this.name(method),
						ErrorCode.InternalError
					);
				}

				if (rpcRp?.error) {
					throw new CallError(this.name(method), rpcRp.error);
				}

				return rpcRp;
			})
			.catch((err: AxiosError | CallError) => {
				if (this.asyncStorageSetter)
					this.asyncStorageSetter(rq.data.id);
				if (err instanceof CallError) {
					throw err;
				}

				throw handleAxiosError(this.name(method), err);
			});
	}

	call(method: string, data?: any): Promise<Response | null> {
		return this._call(method, data, false);
	}

	notify(method: string, data?: any): Promise<Response | null> {
		return this._call(method, data, true);
	}
}
